import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkDataPresentService } from "../services/uploadServices";
import { useClientId } from "./ClientsProvider";

// Create the context
const GlobalContext = createContext();

// Custom hook to use the context
export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

// Provider component that wraps the children components
export const GlobalContextProvider = ({ children }) => {
  const clientId = useClientId();
  const [dataErrors, setDataError] = useState(null);
  const {
    data: dataCheckResult,
    isLoading: isCheckingData,
    isFetching: isFetchingDataCheck,
    refetch,
  } = useQuery({
    queryKey: ["check-data-status", clientId],
    enabled: clientId != null,
    queryFn: async () => {
      const result = await checkDataPresentService(clientId);
      return {
        ...result,
        clientId,
      };
    },
    staleTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (!clientId || dataCheckResult?.clientId !== clientId) {
      setDataError(null);
      return;
    }

    const { clientId: _clientId, ...checkResult } = dataCheckResult;
    setDataError(checkResult);
  }, [clientId, dataCheckResult]);

  const isDataCheckComplete = Boolean(
    clientId &&
    !isCheckingData &&
    !isFetchingDataCheck &&
    dataCheckResult?.clientId === clientId,
  );

  const [isStreaming, setIsStreaming] = useState(false);
  const [fetchListFlag, setFetchListFlag] = useState(false);
  const [transferingInputContent, setTransferingInputContent] = useState("");
  const [isChatStarted, setIsChatStarted] = useState(false);

  return (
    <GlobalContext.Provider
      value={{
        isStreaming,
        setIsStreaming,
        setTransferingInputContent,
        transferingInputContent,
        fetchListFlag,
        setFetchListFlag,
        isChatStarted,
        setIsChatStarted,
        isCheckingData: isCheckingData || isFetchingDataCheck,
        isDataCheckComplete,
        dataCheckClientId: dataCheckResult?.clientId ?? null,
        dataErrors,
        refetchDataCheck: refetch,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
